/**
 * ResponsiveLayout
 *
 * This component acts as a **universal screen wrapper** for both mobile (React Native) and web.
 * It provides:
 *  - Responsive behaviour for cross-platform usage
 *  - A consistent layout structure across devices
 *  - Basic screen layout elements such as:
 *    - Page max width constraints (for large displays)
 *    - Optional footer integration
 *    - Default padding/margins
 *
 * Used internally to wrap `index.tsx` (mobile) and `index.web.tsx` (web) versions
 * of screens into a unified, responsive container.
 *
 * Purpose:
 *  - Remove platform-specific duplication in layout handling
 *  - Ensure visual consistency across screen sizes and devices
 */

import React, { isValidElement, useMemo, useRef, useState } from 'react';
import { Animated, StyleSheet, View, ViewProps } from 'react-native';
import { Text, useTheme } from '@ui-kitten/components';
import lodashIsNull from 'lodash/isNull';
import PropTypes from 'prop-types';

import constants from '../../Config/constants';
import themeStyle from '../../Theme/styles';

import Header, { Header as RawHeader } from '../Header';
import HeaderWithTitleAnimation, {
  HeaderWithTitleAnimationProps,
} from '../HeaderWithTitleAnimation';
import {
  ids as reservationStyleIds,
  styles as reservationStyles,
} from '../Reservation/styles';

import ScreenWrapper, { ScreenWrapperProps } from './';
import type { ScreenWrapperWebProps } from './index.web';

type WrapperProps = ScreenWrapperProps & ScreenWrapperWebProps;
type HeaderProps = PropTypes.InferProps<typeof RawHeader.propTypes> &
  HeaderWithTitleAnimationProps;
type Props = WrapperProps & {
  // this component props needed
  LeftComponent: React.ReactNode;
  MobileBottomComponent?: React.ReactNode; // Right Component will be use if this component pass
  RightComponent?: React.ReactNode;
  rightComponentWidth?: number | string;
  stickyMobile?: boolean;
  withTitleAnimation?: boolean;
  // sub component props
  title?: string | React.ReactNode;
  titleProps?: ViewProps & Partial<HeaderProps>;
};

const ResponsiveLayout = ({
  LeftComponent,
  MobileBottomComponent,
  RightComponent,
  rightComponentWidth = 350,
  stickyMobile,
  title,
  titleProps,
  withTitleAnimation = !constants.isWeb, // can only be true on mobile
  ...rest
}: Props) => {
  const scrollRef = useRef(new Animated.Value(0));
  const theme = useTheme();
  const [stickyBottomHeight, setStickyBottomHeight] = useState(0);

  const isTitleString = typeof title === 'string' && Boolean(title);
  const isTitleElementOrString = React.isValidElement(title) || isTitleString;
  const hasMobileTitle = !constants.isWeb && isTitleElementOrString;
  const hasWebTitle = constants.isWeb && isTitleString;

  const rightContainerStyle = useMemo(
    () => (RightComponent ? { width: rightComponentWidth } : {}),
    [RightComponent, rightComponentWidth],
  );
  const screenWrapperConditionalprops = useMemo(() => {
    if (!withTitleAnimation || constants.isWeb) return {};
    return {
      scrollEventThrottle: constants.scrollThrottle,
      onScroll: Animated.event(
        [
          {
            nativeEvent: {
              contentOffset: {
                y: scrollRef.current,
              },
            },
          },
        ],
        { useNativeDriver: false },
      ),
    };
  }, [withTitleAnimation]);
  const mobileBottomComponent = useMemo(() => {
    const shouldRenderMobile =
      isValidElement(MobileBottomComponent) ||
      lodashIsNull(MobileBottomComponent);
    return shouldRenderMobile ? MobileBottomComponent : RightComponent;
  }, [MobileBottomComponent, RightComponent]);

  return (
    <View style={styles.wrapper}>
      {withTitleAnimation && !constants.isWeb && (
        <HeaderWithTitleAnimation
          iconColor={theme['color-info-500']}
          scroll={scrollRef.current}
          title={title}
          withBanner
          withBorder
          {...titleProps}
        />
      )}
      <ScreenWrapper
        header={
          !withTitleAnimation &&
          hasMobileTitle && (
            <Header
              iconColor={theme['color-info-500']}
              title={title}
              alignTitle="left"
              withBorder
              {...titleProps}
            />
          )
        }
        {...screenWrapperConditionalprops}
        {...rest}
      >
        <View
          dataSet={{ media: reservationStyleIds.contentWrapper }}
          style={[
            reservationStyles.contentWrapper,
            { paddingBottom: stickyBottomHeight },
          ]}
        >
          {/* Left Side & Mobile Bottom Component */}
          <View
            dataSet={{ media: reservationStyleIds.leftContentWrapper }}
            style={reservationStyles.leftContentWrapper}
          >
            {/* Web Page Title */}
            {hasWebTitle && (
              <Text category="h5" style={themeStyle.bold}>
                {title}
              </Text>
            )}
            {/* Left Side */}
            {LeftComponent}
            {/* Mobile Bottom Component */}
            {!stickyMobile && (
              <View
                dataSet={{ media: reservationStyleIds.placeOrderButtonMobile }}
                style={reservationStyles.placeOrderButtonMobile}
              >
                {mobileBottomComponent}
              </View>
            )}
          </View>
          {/* Right Side */}
          <View
            dataSet={{ media: reservationStyleIds.boxWrapperWeb }}
            style={[reservationStyles.boxWrapperWeb, rightContainerStyle]}
          >
            {RightComponent}
          </View>
        </View>
      </ScreenWrapper>
      {stickyMobile && (
        <View
          dataSet={{ media: reservationStyleIds.boxWrapperMobile }}
          style={reservationStyles.boxWrapperMobile}
          onLayout={(event) => {
            if (!constants.isWeb) {
              const { height } = event.nativeEvent.layout;
              setStickyBottomHeight(height);
            }
          }}
        >
          {mobileBottomComponent}
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  wrapper: {
    ...themeStyle.flex1,
    ...(constants.isWeb && { height: '100dvh' }),
  },
});

export default ResponsiveLayout;
